import { t } from '@/i18n';

import type { SmartCalcColumnId as ColumnId } from '../calcStack';
import { toColumnId as asColumnId } from '../calcStack';

export const COL = {
  price: asColumnId('price'),
  pricingModel: asColumnId('pricingModel'),
  delivery: asColumnId('delivery'),
  dailyBudget: asColumnId('dailyBudget'),
  dailyTraffic: asColumnId('dailyTraffic'),
  overallBudget: asColumnId('overallBudget'),
  overallTraffic: asColumnId('overallTraffic'),
  capping: asColumnId('capping'),
} as const satisfies Record<string, ColumnId>;

// A slim column descriptor shape for column-level validation.
// Excludes legacy CalculatorProvider-specific fields (initialLocks, strategy, etc.).

export type PricingColumnConfig = {
  readonly columnId: ColumnId;
  readonly label: string;
  readonly tooltip?: string;
  readonly type?: string;
  readonly autoCalculate?: boolean;
  readonly format?: { readonly prefix?: string; readonly decimals?: number };
  readonly nullable?: boolean;
  readonly min?: number;
  readonly mono?: boolean;
  readonly ui: { readonly width: number; readonly mono?: boolean };
};

/**
 * `tooltip` is an accessor on every entry, not a value. This array is module-level, so a plain
 * `t()` would resolve once at import time and pin the copy to whatever language the app booted
 * in. Every consumer reads it inside a `computed` — `buildSmartColumns` runs in one — so moving
 * the lookup to the read is what lets a locale change rebuild the table. Consumers are unchanged.
 */
export const PRICING_RULE_COLUMNS = [
  {
    columnId: COL.price,
    get label() {
      return t('modules.campaigns.adGroup.columns.price');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.price');
    },
    autoCalculate: false,
    type: 'cascade-value',
    format: { prefix: '$', decimals: 4 },
    min: 0.0001,
    ui: { width: 120, mono: true },
  },
  {
    columnId: COL.pricingModel,
    get label() {
      return t('modules.campaigns.adGroup.columns.model');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.model');
    },
    autoCalculate: false,
    type: 'select',
    ui: { width: 120, mono: true },
  },
  {
    columnId: COL.delivery,
    get label() {
      return t('modules.campaigns.adGroup.columns.delivery');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.delivery');
    },
    autoCalculate: false,
    type: 'select',
    ui: { width: 120, mono: true },
  },
  {
    columnId: COL.dailyBudget,
    get label() {
      return t('modules.campaigns.adGroup.columns.dailyBudget');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.dailyBudget');
    },
    autoCalculate: true,
    nullable: true,
    format: { prefix: '$', decimals: 2 },
    min: 0.1,
    ui: { width: 160, mono: true },
  },
  {
    columnId: COL.overallBudget,
    get label() {
      return t('modules.campaigns.adGroup.columns.overallBudget');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.overallBudget');
    },
    autoCalculate: true,
    nullable: true,
    format: { prefix: '$', decimals: 2 },
    min: 0.1,
    ui: { width: 160, mono: true },
  },
  {
    columnId: COL.dailyTraffic,
    get label() {
      return t('modules.campaigns.adGroup.columns.dailyTraffic');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.dailyTraffic');
    },
    autoCalculate: true,
    nullable: true,
    format: { decimals: 0 },
    min: 1,
    ui: { width: 160, mono: true },
  },
  {
    columnId: COL.overallTraffic,
    get label() {
      return t('modules.campaigns.adGroup.columns.overallTraffic');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.overallTraffic');
    },
    autoCalculate: true,
    nullable: true,
    format: { decimals: 0 },
    min: 1,
    ui: { width: 160, mono: true },
  },
  {
    columnId: COL.capping,
    get label() {
      return t('modules.campaigns.adGroup.columns.freqCap');
    },
    get tooltip() {
      return t('modules.campaigns.hints.adGroup.freqCap');
    },
    autoCalculate: false,
    type: 'nested-object',
    nullable: true,
    ui: { width: 190, mono: true },
  },
] as const satisfies ReadonlyArray<PricingColumnConfig>;

export type DataColumnId = (typeof PRICING_RULE_COLUMNS)[number]['columnId'];

/**
 * The COL PROPERTY NAMES as a literal union — the only exhaustive handle on the pricing columns.
 * `DataColumnId` cannot serve that purpose: `SmartCalcColumnId` is `string & { __brand }`, so
 * `Record<DataColumnId, T>` degrades to an index signature and a missing column compiles clean.
 * Key every per-column dispatch table by THIS type so adding a column to `COL` breaks the build
 * until every stage of the pipeline handles it — a table that silently skipped `capping` is what
 * produced #889, and the same shape had already produced the `pricingModel` fix in a977840b3.
 */
export type PricingColumnKey = keyof typeof COL;
